import React, { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { fetchPreload } from "../redux/slices/preloadSlice";
import { fetchUser, setUserData } from "../redux/slices/userSlice";
import { Link } from "react-router-dom";
import { translateText } from "../assets/translations";
import "./Home.css";
import CardErrorSvg from "../assets/svgs/CardErrorSvg";
import swapUser from "../assets/images/swapUser.png";
import BlueSpin from "../assets/images/BlueSpin.png";
import { useUser } from "../hooks/useUser";
import { cardApi } from "../api/card";
import PageHeader from "./PageHeader";
import { askConfirm } from "../utils/notice";

const Home: React.FC = () => {
  const dispatch = useAppDispatch();
  const user = useUser();
  const { userKey, userToken, loading: userLoading, error: userError } = user;

  const preload = useAppSelector((state) => state.preload.data);
  const preloadLoading = useAppSelector((state) => state.preload.loading);
  const preloadError = useAppSelector((state) => state.preload.error);
  const preloadUsers = preload?.users;

  const [cardsLoadingState, setCardsLoadingState] = useState({ front: "loading", back: "loading" });
  const [userCardKey, setUserCardKey] = useState<number | null>(null);

  useEffect(() => {
    if (!userKey || !userToken) return;

    // Fetch preload data if it's not already loaded
    if (!preload && !preloadLoading && !preloadError) {
      dispatch(fetchPreload());
    }

    // Fetch user data if it's not already loaded or currently loading
    if (!userKey && !userLoading && !userError) {
      dispatch(fetchUser());
    }

    const ownerKey = preloadUsers?.find((u) => u.externalUserKey === userKey)?.userKey;
    const userCard = preload?.cards.find((card) => card.ownerKey === userKey || card.ownerKey === ownerKey);
    setUserCardKey(userCard ? userCard.cardKey : null);
    if (userCard) {
      // 画像が無い面は読み込まない（存在しない画像への 404 を出さない。2026-10-13）
      setCardsLoadingState({ front: userCard.hasFrontImage ? "loading" : "error", back: userCard.hasBackImage ? "loading" : "error" });
    }
  }, [dispatch, userKey, userToken, preload, preloadLoading, preloadError, userLoading, userError]);


  function handleImageLoad(face: "front" | "back", status: "success" | "error") {
    setCardsLoadingState(prevState => ({ ...prevState, [face]: status }));
  }

  const userInfo = ` - ユーザー #${userKey} - ${user.isAdmin ? "管理者" : "一般ユーザー"}`;

  const imageTimestamp = useMemo(() => Date.now(), [userCardKey]);
  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
  const frontCardElement = (cardsLoadingState.front === "error" || userCardKey === null) ? <CardErrorSvg text="前面なし"/> : <Link to={`/camera/${userCardKey}`} state={{ isCaptureModeForces: false }} className="block aspect-[5/3] w-full"><img className="w-full h-full object-cover drop-shadow-lg" src={`${baseUrl}/images/thumbnails/card${userCardKey}front.jpg?reload=${imageTimestamp}`} onLoad={() => { handleImageLoad("front", "success") }} onError={() => { handleImageLoad("front", "error") }} /></Link>;
  const backCardElement = (cardsLoadingState.back === "error" || userCardKey === null) ? <CardErrorSvg text="裏面なし"/> : <Link to={`/camera/${userCardKey}`} state={{ isCaptureModeForces: false }} className="w-full h-full object-cover"><img className="w-full h-full object-cover drop-shadow-lg" src={`${baseUrl}/images/thumbnails/card${userCardKey}back.jpg?reload=${imageTimestamp}`} onLoad={() => { handleImageLoad("back", "success") }} onError={() => { handleImageLoad("back", "error") }} /></Link>;
  

  const myInfoComponent = (

    <div>
      <div className="drop-shadow-md">
        <div className="bg-primary-gray rounded-t-md flex justify-between items-center">
          <h1 className="c-box__title">
            {translateText('自分の名刺', 'english')}{false && userInfo}
          </h1>
          <span className="c-box__meta">{userKey}</span>
        </div>
        {/* ボタン列・前面裏面・削除を等間隔（32px）で縦に並べる */}
        <div className="bg-white rounded-b-md c-box__body myCard__body">
          <div className="flex flex-col md:flex-row justify-start w-full gap-2">
            
            <div className="w-full md:w-40 shrink">
              <Link to={`/camera/${userCardKey}?owner=true&from=scan`} state={{ isCaptureModeForced: true, isUploadOnly: false, isStartEditMode: false }}><div className="btn btn-primary w-full">スキャン</div></Link>
            {/*<Link to={`/camera/${userKey}`} state={{ isCaptureModeForced: true, isUploadOnly: true, isStartEditMode: false }}><div className="btn btn-primary mx-2 mb-2 md:mb-0">画像を選択して登録</div></Link>*/}
            </div>
            
            <div className="w-full md:w-40 shrink">
              <Link to={`/camera/${userCardKey}?owner=true&from=top`} state={{ isCaptureModeForced: false, isUploadOnly: false, isStartEditMode: false }}><div className="btn btn-secondary w-full">閲覧 / 編集</div></Link>
            </div>

          </div>

          <div className="myCard__faces">
            <div className="flex flex-col sm:flex-row gap-4 w-full">
              <div className="w-full sm:w-1/2">
                <div className="text-lg">前面</div>
                {frontCardElement}
              </div>
              <div className="w-full sm:w-1/2">
                <div className="text-lg">裏面</div>
                {backCardElement}
              </div>
            </div>
          </div>

          {/* 削除は取り消せないので、よく使うボタンの並びから離して前面・裏面の下、ボックスの右端に置く（2026-11-02 ニスさん指示） */}
          <div className="myCard__delete">
            <div className="btn btn-danger" onClick={deleteOwnCard}>削除</div>
          </div>
        </div>
      </div>
      {preloadLoading && <span>Loading</span>}
      {preload ? (
        <span style={{ display: "none" }}>Number of cards: {preload.cards.length}</span>
      ) : (
        !preloadLoading && <span>データがありません</span>
      )}
    </div>
  )

  async function deleteOwnCard() {
    const confirmed = await askConfirm({ title: "名刺を削除しますか？", message: "自分の名刺を削除します。\nこの操作は取り消せません。", confirmLabel: "削除する", danger: true });
    if (!confirmed) return;
    if (!userKey || !userCardKey || !userToken) return;

    try {
      // Await the delete operation
      await cardApi.deleteCard(userCardKey, userKey, userToken);

      window.location.reload();

      // Alternative: After successful deletion, fetch the updated data
      //dispatch(fetchPreload());
    } catch (error) {
      console.error("Failed to delete card or fetch updated data:", error);
    }
  }

  const cameraComponent = (
    <div className="drop-shadow-md">
        <div className="bg-primary-gray rounded-t-md">
          <h1 className="c-box__title">{translateText('新しい名刺を作成する', 'english')}</h1>
        {/*<h1 className="text-xl md:text-lg font-bold py-2 pl-5">{translateText('他人の名刺', 'english')}</h1>*/}
      </div>
      <div className="bg-white rounded-b-md c-box__body">
        <div className="flex flex-col md:flex-row justify-start w-full gap-2">
          
          <div className="w-full md:w-40 shrink">
            <Link to={`/camera?from=scan`} state={{ isCaptureModeForced: true, isUploadOnly: false, isStartEditMode: false }}><div className="btn btn-primary w-full">スキャン</div></Link>
          {/*<Link to={`/camera`} state={{ isCaptureModeForced: true, isUploadOnly: true, isStartEditMode: false }}><div className="btn btn-primary mx-2 mb-2">画像を選択して登録</div></Link>*/}
          </div>
          
          <div className="w-full md:w-40 shrink">
            <Link to={`/camera?from=top`} state={{ isCaptureModeForced: false, isUploadOnly: false, isStartEditMode: true }}><div className="btn btn-secondary w-full">手動で登録</div></Link>
          {/* <Link to="/search"><div className="btn btn-primary mx-2 mb-2 md:my-0">My cards</div></Link>
          <Link to="/search"><div className="btn btn-primary mx-2">All cards</div></Link> */}
          </div>

        </div>
      </div>
    </div>
  );

  // 検索を一番上に置く（先方依頼 2026-11-02）。先頭なので上の余白は付けない
  const searchComponent = (
    <div className="drop-shadow-md">
        <div className="bg-primary-gray rounded-t-md">
          <h1 className="c-box__title">{translateText('名刺の検索', 'english')}</h1>
      </div>
      <div className="bg-white rounded-b-md c-box__body">
        <div className="flex flex-col md:flex-row justify-start w-full gap-2">
          <div className="w-full md:w-40 shrink">
            {preloadLoading || !preload ? (
              <button className="btn btn-primary search-loading-button flex items-center justify-center w-full" type="button" disabled aria-label="名刺データを読み込み中">
                <img className="search-loading-spinner" src={BlueSpin} alt="" aria-hidden="true" />
              </button>
            ) : (
              <Link to="/search"><div className="btn btn-primary w-full">名刺を検索</div></Link>
            )}
          {/* <Link to="/search"><div className="btn btn-primary mx-2">All cards</div></Link> */}
          </div>
        </div>
      </div>
    </div>
  );

  const adminComponent = (
    <div className="drop-shadow-md">
        <div className="bg-primary-gray rounded-t-md">
          <h1 className="c-box__title">{translateText('管理者用', 'english')}</h1>
      </div>
      <div className="bg-white rounded-b-md c-box__body">
        <div className="flex flex-col md:flex-row justify-start w-full gap-2">
          <div className="w-full md:w-40 shrink">
            {/*<Link to="/logs"><div className="btn btn-primary mx-2">閲覧履歴</div></Link>*/}
            <Link to="/admin"><div className="btn btn-primary w-full">管理機能</div></Link>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="top-page-container">
      <PageHeader title="名刺" />
      <div className="pageBody homeBoxes">
        {searchComponent}
        {myInfoComponent}
        {cameraComponent}
        {(user.isAdmin || true) ? adminComponent : null}
      </div>
    </div>
  );
};

export default Home;